import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  DEFAULT_JOURNAL_EXPORT_MODE,
  JOURNAL_EXPORT_MODE_OPTIONS,
  type JournalExportMode,
} from '@/lib/journal/journalExportMode';
import { FORM_HINT_TYPE, TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';

interface JournalExportModalProps {
  busy: boolean;
  onClose: () => void;
  onConfirm: (mode: JournalExportMode) => void;
}

export function JournalExportModal({ busy, onClose, onConfirm }: JournalExportModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<JournalExportMode>(DEFAULT_JOURNAL_EXPORT_MODE);

  return (
    <Modal title={t('journal.export_modal.title')} onClose={onClose}>
      <div className="space-y-4" data-testid="journal-export-modal">
        <p className={UI_MUTED}>{t('journal.export_modal.choose')}</p>
        <div className="space-y-2" role="radiogroup" aria-label={t('journal.export_modal.choose')}>
          {JOURNAL_EXPORT_MODE_OPTIONS.map((option) => {
            const selected = mode === option.mode;
            return (
              <label
                key={option.mode}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3',
                  selected
                    ? 'border-ds-teal bg-ds-teal-tint'
                    : 'border-ds-line',
                )}
              >
                <input
                  type="radio"
                  name="journal-export-mode"
                  value={option.mode}
                  checked={selected}
                  onChange={() => setMode(option.mode)}
                  className="mt-1 accent-ds-teal"
                  data-testid={option.testId}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className={cn(TYPE.body, 'font-medium text-ds-text')}>
                      {t(option.titleKey)}
                    </span>
                    {option.recommended ? (
                      <span
                        className={cn(
                          FORM_HINT_TYPE,
                          'shrink-0 text-ds-teal',
                        )}
                        data-testid="journal-export-recommended"
                      >
                        {t('journal.export_modal.recommended')}
                      </span>
                    ) : null}
                  </span>
                  <span className={cn(UI_MUTED, 'mt-1 block')}>{t(option.descriptionKey)}</span>
                </span>
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(mode)}
            disabled={busy}
            data-testid="journal-export-confirm"
          >
            {busy ? t('journal.export_modal.exporting') : t('journal.export_modal.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
